import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { cn } from '@workspace/ui/lib/utils'
import { Check, Loader2 } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { OnboardingLayout } from './onboarding-layout.js'

export interface ProcessingTask {
  label: string
  run: () => Promise<void>
}

interface ProcessingScreenProps {
  title: string
  subtitle: string
  tasks: ProcessingTask[]
  onComplete: () => void
  onError: (error: string) => void
  /** Optional time-estimate hint shown under the subtitle (e.g. "about a minute"). */
  estimate?: string
  /** Shown under the progress list (the sign-up question). */
  children?: ReactNode
  /**
   * Hold the screen instead of the automatic jump: while the question is still
   * loading, and then until Continue (shown once `children` is on screen), so
   * nobody is pulled away mid-answer.
   */
  waitForContinue?: boolean
}

export function ProcessingScreen({
  title,
  subtitle,
  tasks,
  onComplete,
  onError,
  estimate,
  children,
  waitForContinue = false,
}: ProcessingScreenProps) {
  const [completedCount, setCompletedCount] = useState(0)
  const [allDone, setAllDone] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    async function runTasks() {
      for (const [i, task] of tasks.entries()) {
        try {
          await task.run()
          setCompletedCount(i + 1)
        } catch (err) {
          onError(err instanceof Error ? err.message : 'Something went wrong')
          return
        }
      }
      setAllDone(true)
    }

    runTasks()
  }, [tasks, onError])

  useEffect(() => {
    if (!allDone || waitForContinue) return
    const timer = setTimeout(onComplete, 600)
    return () => clearTimeout(timer)
  }, [allDone, onComplete, waitForContinue])

  return (
    <OnboardingLayout centerVertically maxWidth={children ? 'max-w-md' : 'max-w-sm'}>
      <div className="text-center">
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
        {estimate ? (
          <p className="mt-1 text-xs text-muted-foreground/70">This usually takes {estimate}.</p>
        ) : null}

        <div className="mt-8 flex flex-col items-center gap-3">
          {tasks.map((task, i) => {
            const done = i < completedCount
            const active = i === completedCount && !allDone

            return (
              <div key={task.label} className="relative">
                {done ? (
                  <Check className="absolute -left-6 top-1/2 size-3.5 -translate-y-1/2 text-emerald-400" />
                ) : active ? (
                  <Loader2 className="absolute -left-6 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-[#f97316]" />
                ) : null}
                <span className={cn('text-sm', done ? 'text-foreground' : 'text-muted-foreground')}>
                  {task.label}
                </span>
              </div>
            )
          })}
        </div>

        {children}

        {waitForContinue && allDone && children ? (
          <CtaButton className="mt-6 w-full" onClick={onComplete}>
            Continue
          </CtaButton>
        ) : null}
      </div>
    </OnboardingLayout>
  )
}
